// Entry point: one QueryClient, the code-based router, and the session hooks the API client needs
// (what to do when the session expires mid-use, and how to refresh a rejected CSRF token).
import { QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider } from '@tanstack/react-router'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import './styles.css'
import { setCsrfRefresher, setCsrfToken, setUnauthorizedHandler } from './lib/api'
import { safeReturnTo } from './lib/auth'
import { meQueryOptions } from './lib/auth-context'
import { createQueryClient } from './lib/query-client'
import { createAppRouter } from './router'

const queryClient = createQueryClient()
const router = createAppRouter(queryClient)

// The session ended while the app was open (expired, revoked, signed out in another tab): drop cached data and
// send the person to sign in again, returning them to the page they were on.
let redirecting = false
setUnauthorizedHandler(() => {
  const loc = router.state.location
  if (redirecting || loc.pathname === '/login') return
  redirecting = true
  setCsrfToken(null)
  queryClient.clear()
  void router
    .navigate({
      to: '/login',
      search: { returnTo: safeReturnTo(loc.href), error: 'session_expired' },
    } as never)
    .finally(() => {
      redirecting = false
    })
})

// The server rejected our CSRF token (session refreshed in another tab): read the current one and retry once.
setCsrfRefresher(async () => {
  const me = await queryClient.fetchQuery({ ...meQueryOptions, staleTime: 0 })
  return me.csrfToken
})

const container = document.getElementById('root')
if (!container) throw new Error('index.html must contain <div id="root"></div>')

createRoot(container).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
)
